/**
 * UsuariosListPage (F84) — admin de usuarios del sistema.
 *
 * Lista + filtros (rol, activo, search) + stats cards + acciones:
 *   editar, permisos override, regenerar password, reset 2FA, desactivar.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserCog, Plus, Search, Pencil, Trash2, RefreshCw, Shield, ShieldOff,
  KeyRound, Filter,
} from "lucide-react";

import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

import {
  useBorrarUsuario, useRegenerarPassword, useReset2FA, useRoles,
  useUsuarios, useUsuariosStats,
} from "./api";
import type { User, UserCreatedResponse } from "./schemas";
import { UsuarioFormDialog } from "./UsuarioFormDialog";
import { UsuarioPermisosDialog } from "./UsuarioPermisosDialog";
import { PasswordRevealedModal } from "./PasswordRevealedModal";

export function UsuariosListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [rolId, setRolId] = useState<string | "">("");
  const [activoFilter, setActivoFilter] = useState<"all" | "activos" | "inactivos">("all");

  const { data: roles = [] } = useRoles();
  const { data: stats } = useUsuariosStats();

  const filters = useMemo(() => ({
    page, size: 50, search,
    rol_id: rolId || null,
    activo: activoFilter === "all" ? null : activoFilter === "activos",
  }), [page, search, rolId, activoFilter]);

  const { data, isLoading, isFetching } = useUsuarios(filters);
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [permsUserId, setPermsUserId] = useState<string | null>(null);
  const [credShow, setCredShow] = useState<UserCreatedResponse | null>(null);

  const regenMut = useRegenerarPassword();
  const reset2faMut = useReset2FA();
  const borrarMut = useBorrarUsuario();
  // F104.1 — guard self-modify
  const currentUserId = useAuthStore((s) => s.user?.id);

  async function handleRegenerar(u: User) {
    if (!confirm(
      `¿Regenerar password de "${u.email}"?\n\n` +
      "El usuario actual no podrá entrar hasta que le des la nueva. " +
      "Al primer login se le forzará a cambiarla.",
    )) return;
    try {
      const res = await regenMut.mutateAsync({ id: u.id });
      setCredShow(res);
    } catch (e) {
      toast.error("No se pudo regenerar", { description: (e as ApiError).message });
    }
  }

  async function handleReset2FA(u: User) {
    if (!confirm(
      `¿Desactivar 2FA de "${u.email}"?\n\n` +
      "El usuario quedará sin segundo factor. Puede volver a activarlo " +
      "desde su perfil cuando entre.",
    )) return;
    try {
      await reset2faMut.mutateAsync(u.id);
      toast.success("2FA desactivado");
    } catch (e) {
      toast.error("No se pudo resetear 2FA", { description: (e as ApiError).message });
    }
  }

  async function handleBorrar(u: User) {
    if (!confirm(
      `¿Desactivar al usuario "${u.email}"?\n\n` +
      "Es soft-delete: los registros que el usuario haya creado se mantienen, " +
      "pero ya no podrá entrar al sistema. Si después lo necesitas, " +
      "tendría que crearse de nuevo.",
    )) return;
    try {
      await borrarMut.mutateAsync(u.id);
      toast.success("Usuario desactivado");
    } catch (e) {
      toast.error("No se pudo desactivar", { description: (e as ApiError).message });
    }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserCog size={16} /> Usuarios del sistema
          </h2>
          <p className="text-xs text-muted-foreground max-md:hidden">
            Administra cuentas, roles y permisos personalizados.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> <span className="max-md:hidden">Nuevo usuario</span><span className="md:hidden">Nuevo</span>
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Activos" value={stats.activos} accent="emerald" />
          <StatCard label="Inactivos" value={stats.inactivos} />
          <StatCard label="Con 2FA" value={stats.con_2fa} accent="primary" />
          <StatCard label="Sin login 30d" value={stats.sin_login_30d} accent="amber" />
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Buscar por email o nombre…"
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-1.5 max-md:w-full max-md:[&>select]:min-w-0 max-md:[&>select]:flex-1">
          <Filter size={12} className="text-muted-foreground" />
          <Select
            value={rolId}
            onChange={(e) => { setRolId(e.target.value); setPage(1); }}
            className="text-xs"
          >
            <option value="">Todos los roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.nombre}</option>
            ))}
          </Select>
          <Select
            value={activoFilter}
            onChange={(e) => { setActivoFilter(e.target.value as typeof activoFilter); setPage(1); }}
            className="text-xs"
          >
            <option value="all">Activos + Inactivos</option>
            <option value="activos">Solo activos</option>
            <option value="inactivos">Solo inactivos</option>
          </Select>
        </div>
      </div>

      {/* rc140 — móvil: tarjetas */}
      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>Sin usuarios con esos filtros.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((u) => (
              <MobileCard
                key={u.id}
                title={<>{u.nombre_completo ?? <span className="italic text-muted-foreground">sin nombre</span>}{u.password_change_required && <Badge variant="secondary" className="ml-1.5 text-[9px]">cambiar pass</Badge>}</>}
                subtitle={<span className="font-mono">{u.email}</span>}
                right={u.activo ? <Badge variant="success">activo</Badge> : <Badge variant="secondary" className="opacity-60">inactivo</Badge>}
                actions={
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(u)}><Pencil size={13} /> Editar</Button>
                    <Button variant="ghost" size="sm" onClick={() => setPermsUserId(u.id)}><Shield size={13} /> Permisos</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleRegenerar(u)} disabled={regenMut.isPending} title="Regenerar password"><RefreshCw size={13} /></Button>
                    {u.tfa_enabled && <Button variant="ghost" size="sm" onClick={() => handleReset2FA(u)} disabled={reset2faMut.isPending} title="Desactivar 2FA"><KeyRound size={13} /></Button>}
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleBorrar(u)} disabled={borrarMut.isPending || u.id === currentUserId} title="Desactivar usuario"><Trash2 size={13} /></Button>
                  </>
                }
              >
                <MobileMeta label="Rol">{u.rol_nombre ? <Badge variant="outline" className="capitalize">{u.rol_nombre}</Badge> : <span className="italic text-muted-foreground">sin rol</span>}</MobileMeta>
                <MobileMeta label="2FA">{u.tfa_enabled ? <span className="inline-flex items-center gap-1 text-emerald-600"><Shield size={12} /> activo</span> : <span className="inline-flex items-center gap-1 text-muted-foreground"><ShieldOff size={12} /> no</span>}</MobileMeta>
                <MobileMeta label="Último login" span>{u.last_login_at ? formatDateTime(u.last_login_at) : "Nunca"}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estado</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Último login</TableHead>
              <TableHead>2FA</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={UserCog}
                  title="Sin usuarios"
                  description={
                    search || rolId || activoFilter !== "all"
                      ? "Sin coincidencias con los filtros."
                      : "Crea el primer usuario admin del sistema."
                  }
                  action={
                    !search && !rolId && activoFilter === "all" ? (
                      <Button size="sm" onClick={() => setCreateOpen(true)}>
                        <Plus size={14} /> Nuevo usuario
                      </Button>
                    ) : undefined
                  }
                />
              </TableEmpty>
            ) : (
              items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    {u.activo ? (
                      <Badge variant="success">activo</Badge>
                    ) : (
                      <Badge variant="secondary" className="opacity-60">inactivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>
                    {u.nombre_completo ?? <span className="text-muted-foreground italic">—</span>}
                    {u.password_change_required && (
                      <Badge variant="secondary" className="ml-1.5 text-[9px]">
                        cambiar pass
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.rol_nombre ? (
                      <Badge variant="outline" className="capitalize">{u.rol_nombre}</Badge>
                    ) : (
                      <span className="text-muted-foreground italic text-xs">sin rol</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : "Nunca"}
                  </TableCell>
                  <TableCell>
                    {u.tfa_enabled ? (
                      <Shield size={14} className="text-emerald-500" />
                    ) : (
                      <ShieldOff size={14} className="text-muted-foreground/40" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => setEditing(u)}
                        title="Editar (email, nombre, rol, activar/desactivar)"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => setPermsUserId(u.id)}
                        title="Ver / editar permisos personalizados"
                      >
                        <Shield size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleRegenerar(u)}
                        disabled={regenMut.isPending}
                        title="Regenerar password — admin la entrega al usuario"
                      >
                        <RefreshCw size={14} />
                      </Button>
                      {u.tfa_enabled && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => handleReset2FA(u)}
                          disabled={reset2faMut.isPending}
                          title="Desactivar 2FA del usuario (si lo perdió)"
                        >
                          <KeyRound size={14} />
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleBorrar(u)}
                        disabled={borrarMut.isPending || u.id === currentUserId}
                        title={
                          u.id === currentUserId
                            ? "No puedes desactivar tu propia cuenta"
                            : "Desactivar usuario (soft-delete)"
                        }
                      >
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
              {isFetching && " · actualizando…"}
            </span>
            <div className="flex gap-1">
              <Button
                size="sm" variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >Anterior</Button>
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Dialogs */}
      <UsuarioFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        usuario={null}
        onCreated={(res) => { setCreateOpen(false); setCredShow(res); }}
      />
      <UsuarioFormDialog
        open={editing !== null}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        usuario={editing}
        onCreated={() => setEditing(null)}
      />
      <UsuarioPermisosDialog
        userId={permsUserId}
        open={permsUserId !== null}
        onOpenChange={(v) => { if (!v) setPermsUserId(null); }}
      />
      <PasswordRevealedModal
        cred={credShow}
        onClose={() => setCredShow(null)}
      />
    </div>
  );
}

// ─── Sub-componentes ────────────────────────────────────────────────────
function StatCard({
  label, value, accent,
}: {
  label: string; value: number;
  accent?: "emerald" | "amber" | "primary";
}) {
  const accentClass =
    accent === "emerald" ? "text-emerald-600 dark:text-emerald-400" :
    accent === "amber"   ? "text-amber-600 dark:text-amber-400" :
    accent === "primary" ? "text-primary" :
    "";
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`text-2xl font-semibold ${accentClass}`}>{value}</div>
    </div>
  );
}
